import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import {
  Antenna, Plus, Trash2, Link2, Pencil, X, Save, Loader2,
  Pause, Play, RefreshCw,
} from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'HLS', label: 'HLS' },
  { value: 'RTMP', label: 'RTMP' },
  { value: 'MPEG-TS', label: 'MPEG-TS' },
  { value: 'DASH', label: 'DASH' },
];

const STATUS_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'testing', label: 'Test' },
];

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSrc, setNewSrc] = useState({ name: '', type: 'HLS', url: '', status: 'online' });
  const [loading, setLoading] = useState(true);
  const [editSrc, setEditSrc] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [syncingId, setSyncingId] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/sources');
      setSources(res.data);
    } catch (err) {
      console.error('Kaynak yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sources', newSrc);
      setShowAddModal(false);
      setNewSrc({ name: '', type: 'HLS', url: '', status: 'online' });
      load();
      toast.success('Yayın kaynağı eklendi ve kanallara senkronize edildi');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kaynak eklenemedi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaynağı silmek istediğinize emin misiniz? Bağlı kanal da silinebilir.')) return;
    try {
      await api.delete(`/sources/${id}`);
      setSources(sources.filter(s => s._id !== id));
      toast.success('Kaynak ve bağlı kanal silindi');
    } catch (err) {
      toast.error('Silinemedi.');
    }
  };

  const togglePause = async (src) => {
    const nextStatus = src.status === 'online' ? 'offline' : 'online';
    try {
      const res = await api.patch(`/sources/${src._id}`, { status: nextStatus });
      setSources(prev => prev.map(s => (s._id === src._id ? { ...s, ...res.data } : s)));
      toast.success(nextStatus === 'offline' ? 'Kaynak duraklatıldı' : 'Kaynak yayına alındı');
    } catch (err) {
      toast.error('İşlem başarısız.');
    }
  };

  const handleSync = async (src) => {
    setSyncingId(src._id);
    try {
      const res = await api.post(`/sources/${src._id}/sync`, {});
      setSources(prev => prev.map(s => (s._id === src._id ? { ...s, ...res.data } : s)));
      if (res.data.status === 'online') {
        toast.success(`${src.name}: Bağlantı OK`);
      } else {
        toast.error(`${src.name}: Erişilemedi`);
      }
    } catch (err) {
      toast.error('Sync başarısız.');
    } finally {
      setSyncingId('');
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditBusy(true);
    try {
      const { _id, ...patch } = editSrc;
      const res = await api.patch(`/sources/${_id}`, patch);
      setSources(prev => prev.map(s => (s._id === _id ? { ...s, ...res.data } : s)));
      setEditSrc(null);
      toast.success('Kaynak güncellendi');
    } catch (err) {
      toast.error('Güncellenemedi.');
    } finally { setEditBusy(false); }
  };

  const maskUrl = (url) => {
    if (!url) return '—';
    try {
      const u = new URL(url);
      return `${u.protocol}//${u.hostname.replace(/[^.]/g, '*')}${u.port ? ':***' : ''}/***`;
    } catch { return '***'; }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Antenna className="text-amber-500" /> Yayın Kaynakları V1.9.0
          </h1>
          <button onClick={() => setShowAddModal(true)} className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2">
            <Plus size={20} /> Yeni Kaynak Ekle
          </button>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-white">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-4">Kaynak Adı</th>
                <th className="p-4">Tip</th>
                <th className="p-4">URL</th>
                <th className="p-4">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="5" className="p-12 text-center text-gray-500 italic">Yükleniyor…</td></tr>
              ) : sources.map(src => (
                <tr key={src._id} className="hover:bg-slate-800/30 transition-colors group">
                  <td className="p-4 font-bold text-white">{src.name}</td>
                  <td className="p-4"><span className="text-[10px] font-mono px-2 py-1 bg-slate-800 text-amber-400 rounded border border-slate-700 uppercase">{src.type}</span></td>
                  <td className="p-4"><code className="font-mono text-xs text-gray-400 truncate block max-w-[300px]">{maskUrl(src.url)}</code></td>
                  <td className="p-4">
                    <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase ${src.status === 'online' ? 'text-green-500' : 'text-red-500'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${src.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                      {src.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleSync(src)} disabled={syncingId === src._id} className="text-gray-500 hover:text-blue-400 p-2 rounded-lg" title="Sync">
                        {syncingId === src._id ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                      </button>
                      <button onClick={() => togglePause(src)} className="p-2 rounded-lg" title={src.status === 'online' ? 'Duraklat' : 'Başlat'}>
                        {src.status === 'online' ? <Pause className="text-amber-400" size={18} /> : <Play className="text-green-400" size={18} />}
                      </button>
                      <button onClick={() => setEditSrc(src)} className="text-gray-500 hover:text-amber-400 p-2 rounded-lg" title="Düzenle"><Pencil size={18} /></button>
                      <button onClick={() => handleDelete(src._id)} className="text-gray-500 hover:text-red-500 p-2 rounded-lg" title="Sil"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
